import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import { SectionContainer } from '../Components/SectionContainer';
import { SectionHeader } from '../Components/SectionHeader';

interface UsesItemProps {
    name: string;
    description?: string;
    link?: string;
}

function UsesItem({ name, description, link }: UsesItemProps) {
    return (
        <Box sx={{ marginBottom: '16px' }}>
            <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: 600, color: '#1e293b' }}
            >
                {link ? <Link href={link}>{name}</Link> : name}
            </Typography>
            {description && (
                <Typography
                    variant="body1"
                    component="span"
                    sx={{ color: '#334155' }}
                >
                    {' '}- {description}
                </Typography>
            )}
        </Box>
    );
}

interface UsesSectionProps {
    title: string;
    items: UsesItemProps[];
}

function UsesSection({ title, items }: UsesSectionProps) {
    return (
        <Box sx={{ marginBottom: { xs: '32px', sm: '48px' } }}>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px',
                }}
            >
                {title}
            </Typography>
            {items.map((item, index) => (
                <UsesItem key={index} {...item} />
            ))}
        </Box>
    );
}

export function UsesPage() {
    const hardware = [
        { name: 'MacBook Pro 14"', description: 'M3 Pro, my daily driver' },
        { name: 'LG 38" Monitor', description: 'Clean and sharp for coding' },
        { name: 'Apple Magic Trackpad', description: 'Gestures over mouse' },
        { name: 'AirPods (3rd gen)', description: 'For music and calls' },
    ];

    const development = [
        { name: 'Cursor', description: 'Primary editor', link: 'https://cursor.com/' },
        { name: 'Claude Code', description: 'AI coding assistant', link: 'https://claude.ai/' },
        { name: 'IntelliJ IDEA', description: 'For Java and Kotlin', link: 'https://www.jetbrains.com/idea/' },
        { name: 'iTerm2', description: 'Terminal of choice', link: 'https://iterm2.com/' },
    ];

    const apps = [
        { name: 'Notion', description: 'Notes and docs', link: 'https://notion.com/' },
        { name: 'Spotify', description: 'Music while coding', link: 'https://spotify.com/' },
        { name: 'Monosnap', description: 'Screenshots', link: 'https://monosnap.ai/' },
    ];

    const stick = [
        { name: 'Babolat Pure Strike 2025 16x19', description: 'Current racket' },
        { name: 'Tennis Warehouse Leather Grip', description: 'Replacement grip' },
        { name: 'Solinco Confidential', description: 'String of choice' },
    ];

    return (
        <SectionContainer>
            <SectionHeader header="What I Use" />
            <Typography
                variant="subtitle1"
                sx={{ marginBottom: { xs: '32px', sm: '48px' }, color: '#334155' }}
            >
                A living document of the tools, apps, and hardware I use daily.
            </Typography>

            <UsesSection title="Hardware" items={hardware} />
            <UsesSection title="Development" items={development} />
            <UsesSection title="Apps" items={apps} />
            <UsesSection title="Stick 🎾" items={stick} />
        </SectionContainer>
    );
}
